import {
  View,
  Text,
  Svg,
  Path,
  StyleSheet,
  Defs,
  ClipPath,
  Rect,
  G,
  Link,
  Image,
} from "@react-pdf/renderer";
import { useState, useEffect } from "react";

const styles = StyleSheet.create({
  imageContainer: {
    marginVertical: 10,
    border: "1px solid #e0e0e0",
    padding: 8,
    borderRadius: 4,
    maxWidth: 300,
  },
  pdfImage: {
    maxWidth: "100%",
    maxHeight: 200,
    marginBottom: 5,
  },
  downloadLink: {
    color: "#1890ff",
    textDecoration: "underline",
    fontSize: 10,
  },
  errorText: {
    color: "#ff4d4f",
    fontSize: 10,
  },
  text: {
    fontSize: 14,
    marginBottom: 10,
    fontWeight: "400",
  },
  marginLeft_10: {
    marginLeft: "10px",
  },
  marginTop_10: {
    marginTop: "10px",
  },
});

const PartialStar = ({ fillPercentage }) => (
  <Svg width="16" height="16" viewBox="0 0 24 24">
    {/* Gray background */}
    <Path
      fill="#CCCCCC"
      d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z"
    />
    {/* Yellow clipped portion */}
    <Defs>
      <ClipPath id="partialClip">
        <Rect x="0" y="0" width={24 * fillPercentage} height="24" />
      </ClipPath>
    </Defs>
    <G clipPath="url(#partialClip)">
      <Path
        fill="#FFD700"
        d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z"
      />
    </G>
  </Svg>
);

// Full Star Component
const FullStar = ({ filled }) => (
  <Svg width="16" height="16" viewBox="0 0 24 24">
    <Path
      fill={filled ? "#FFD700" : "#CCCCCC"}
      d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z"
    />
  </Svg>
);

// Complete StarRating Component
export const StarRating = ({ rating, maxStars = 5 }) => {
  const fullStars = Math.floor(rating);
  const partialFill = rating % 1;
  const emptyStars = maxStars - Math.ceil(rating);

  return (
    <View style={{ flexDirection: "row", alignItems: "center" }}>
      {/* Full stars */}
      {Array(fullStars)
        .fill(0)
        .map((_, i) => (
          <FullStar key={`full-${i}`} filled={true} />
        ))}

      {/* Partial star */}
      {partialFill > 0 && <PartialStar fillPercentage={partialFill} />}

      {/* Empty stars */}
      {Array(emptyStars)
        .fill(0)
        .map((_, i) => (
          <FullStar key={`empty-${i}`} filled={false} />
        ))}

      {/* Rating text (optional) */}
      <Text style={{ marginLeft: 5, fontSize: "12px" }}>{rating}</Text>
    </View>
  );
};

const Checkbox = ({ checked, label }) => (
  <View style={{ flexDirection: "row", alignItems: "center", marginBottom: 5 }}>
    <Svg width="14" height="14" viewBox="0 0 24 24">
      {/* Box outline */}
      <Path
        d="M19 5v14H5V5h14m0-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2z"
        stroke="#000"
        fill="none"
      />
      {/* Check mark (conditionally shown) */}
      {checked && (
        <Path
          d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"
          fill="#000"
        />
      )}
    </Svg>
    <Text style={[styles.marginLeft_10, styles.marginTop_10, styles.text]}>
      {label}
    </Text>
  </View>
);

export const CheckboxGroup = ({ options, selected }) => (
  <View>
    {options.map((option) => (
      <Checkbox
        key={option}
        checked={selected.includes(option)}
        label={option}
      />
    ))}
  </View>
);

export const RadioGroup = ({ options, selectedValue }) => (
  <View>
    {options.map((option) => (
      <View
        key={option}
        style={{ flexDirection: "row", alignItems: "center", marginBottom: 5 }}
      >
        <Svg width="14" height="14" viewBox="0 0 24 24">
          <Path
            d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8z"
            fill="none"
            stroke="#000"
          />
          {selectedValue === option && (
            <Path
              d="M12 7c-2.76 0-5 2.24-5 5s2.24 5 5 5 5-2.24 5-5-2.24-5-5-5z"
              fill="#000"
            />
          )}
        </Svg>
        <Text style={[styles.marginLeft_10, styles.marginTop_10, styles.text]}>
          {option}
        </Text>
      </View>
    ))}
  </View>
);


const fetchImageAsBase64 = async (imageUrl) => {
  try {
    const response = await fetch(imageUrl);
    const blob = await response.blob();
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    console.error("Failed to convert image to Base64:", error);
    return null;
  }
};

export const PdfImageWithDownload = ({ fileData }) => {
  const [imageSrc, setImageSrc] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    const loadImage = async () => {
      try {
        if (!fileData?.fileName) {
          setError(true);
          return;
        }

        const encodedUrl = encodeURI(fileData.fileName);

        const base64 = await fetchImageAsBase64(encodedUrl);

        if (base64) {
          setImageSrc(base64);
        } else {
          setError(true);
        }
      } catch (err) {
        console.error("Image loading failed:", err);
        setError(true);
      }
    };

    loadImage();
  }, [fileData]);

  if (error) {
    return (
      <View style={styles.imageContainer}>
        <Text style={styles.errorText}>Image could not be loaded</Text>
        <Image src={fileData?.fileName} style={styles.pdfImage} alt="Document Image" />

        <DownloadLink url={encodeURI(fileData?.fileName)} label={"Click here to download the image"} />
        {/* <Link src={encodeURI(fileData?.fileName || "")}>
          <Text style={styles.downloadLink}>
            Click here to download the image
          </Text>
        </Link> */}
      </View>
    );
  }

  return (
    <View style={styles.imageContainer}>
      {/* PDF Image (Base64 ensures it appears) */}
      {imageSrc && (
        <Link src={encodeURI(fileData.fileName)}>
          <Image src={imageSrc} style={styles.pdfImage} alt="Document Image" />
        </Link>
      )}

      {/* Download link (opens in new tab) */}
      {/* <Link src={encodeURI(fileData.fileName)}>
        <Text style={styles.downloadLink}>
          {imageSrc
            ? "Click image to open original in new tab"
            : "Download Image"}
        </Text>
      </Link> */}

      <DownloadLink url={encodeURI(fileData.fileName)} label={imageSrc
            ? "Click image to open original in new tab"
            : "Download Image"} />
    </View>
  );
};

const DownloadLink = ({ url, label }) => {
  const downloadUrl = `${url}?openInNewTab=1`;

  return (
    <Link src={downloadUrl}>
      <Text style={{ color: 'blue', textDecoration: 'underline', fontSize: "14px" }}>
        {label || 'Open in new tab'}
      </Text>
    </Link>
  );
};